import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { FeatureHintsProvider } from 'components/shared/feature-hints';
import type {
  FeatureHintStep,
  FeatureHintDismissReason,
} from 'components/shared/feature-hints';

import AiChat from './components/chat/ai-chat';
import SessionsSidebar from './components/sessions-sidebar/sessions-sidebar';
import { getMarkHintsSteps } from './components/walkthrough/mark-hints.config';
import { MARK_START_HINTS_EVENT } from './ai-assistant-events';
import {
  trackMarkWalkthroughCompleted,
  trackMarkWalkthroughDismissed,
  trackMarkWalkthroughViewed,
} from './analytics/mark-walkthrough-analytics';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useTokenUsage } from './hooks/use-token-usage';
import { useMarkReadOnly } from './hooks/use-mark-read-only';

import { getIsTokenUsageExempt } from './helpers/token-usage-helpers';

import { AiAssistantSessionProvider } from './context/ai-assistant-session-context';

const AiAssistantPage = (): React.JSX.Element => {
  const { data: currentUser } = useCurrentUser();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const focusMessageInputRef = useRef<(() => void) | null>(null);

  const clientId = (params.clientId ||
    (currentUser as unknown as { client?: { id: string | number } })?.client
      ?.id) as string | number;
  const isReadOnly = useMarkReadOnly();
  const conversationId = searchParams.get('conversationId');

  const isTokenUsageExempt = getIsTokenUsageExempt(currentUser);
  const { hasUsageData, isError } = useTokenUsage({
    oktaUserId: currentUser?.oktaUserId ?? '',
    enabled: !isTokenUsageExempt,
  });
  const isUsageHintEnabled = !isTokenUsageExempt && (hasUsageData || isError);
  const hintSteps = useMemo(
    () => getMarkHintsSteps(isUsageHintEnabled),
    [isUsageHintEnabled]
  );

  useEffect(() => {
    focusMessageInputRef.current?.();
  }, []);

  const handleToggleSidebar = (): void => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleHintStepViewed = useCallback(
    (step: FeatureHintStep, stepIndex: number): void => {
      trackMarkWalkthroughViewed({
        section: 'hintTour',
        stepNumber: stepIndex + 1,
        stepId: step.id,
      });
    },
    []
  );

  const handleHintTourDismissed = useCallback(
    (
      _step: FeatureHintStep,
      stepIndex: number,
      reason: FeatureHintDismissReason
    ): void => {
      trackMarkWalkthroughDismissed({
        section: 'hintTour',
        dismissMethod: reason,
        stepNumber: stepIndex + 1,
      });
    },
    []
  );

  const handleHintTourCompleted = useCallback((): void => {
    trackMarkWalkthroughCompleted({
      section: 'hintTour',
      completionType: 'hintsFinished',
    });
  }, []);

  return (
    <AiAssistantSessionProvider>
      <FeatureHintsProvider
        steps={hintSteps}
        startEventName={MARK_START_HINTS_EVENT}
        onStepViewed={handleHintStepViewed}
        onTourDismissed={handleHintTourDismissed}
        onTourCompleted={handleHintTourCompleted}
      >
        <div className="flex flex-col flex-1 w-full p-4 bg-navy-50 mb-[-24px]">
          <div className="relative flex flex-row flex-1 border rounded-lg shadow-xs bg-white">
            <SessionsSidebar
              isOpen={isSidebarOpen}
              onToggle={handleToggleSidebar}
              conversationId={conversationId}
              setSearchParams={setSearchParams as never}
              clientId={clientId}
              onFocusMessageInput={() => focusMessageInputRef.current?.()}
              isReadOnly={isReadOnly}
            />
            <AiChat
              isSidebarOpen={isSidebarOpen}
              conversationId={conversationId}
              setSearchParams={setSearchParams as never}
              clientId={clientId}
              registerFocusMessageInput={(fn) => {
                focusMessageInputRef.current = fn;
              }}
              isReadOnly={isReadOnly}
            />
          </div>
        </div>
      </FeatureHintsProvider>
    </AiAssistantSessionProvider>
  );
};

export default AiAssistantPage;
